import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { alertActions, filehubAction } from "_store";
import { fileExtension, fileSizeReadable, fileTypeAcceptable, convertToBase64, base64ToFile, } from '_utils';
import * as XLSX from 'xlsx';
// import { materialsymbolsupload } from 'images';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { IconButton } from "@material-ui/core";
import { materialsymbolsupload, materialsymbolsdownload, Delete } from 'images';
import {useCheckRole } from "_utils/role-access";
import { ERR_MSG } from "constants/labels";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const FirmUpload = ({
    actualData,
    multiple = true,
    supportedFormats,
    data,
    setData,
    setSelectedDate,
    setIsSaveButtonEnabled
}) => {
    const header = "Firm";
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [fileNames, setFileNames] = useState([]);
    const hasUpdateSummaryAdjustmentsByFirmMarketerGroupsAccess = useCheckRole(4,"UpdateSummaryAdjustmentsByFirmMarketerGroups")

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const handleError = (error, file) => {
        dispatch(alertActions.error(error.message));
        setIsSaveButtonEnabled(false);
    };

    const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const convertToNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        return parseInt(String(value).replace(/,/g, ''), 10) || 0;
    };

    const downloadSample = async () => {
        dispatch(alertActions.clear());
        let fileName = "Summary Adjustment Activity Firm.xlsx";

        try {
            await dispatch(
                filehubAction.downloadFile({
                    fileNames: [fileName],
                    fileType: "Adjustment Activity",
                })
            ).unwrap();
        } catch (error) {
            dispatch(
                alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header: `${header} Failed`,
                }),
            );
        }
    };

    const handleChange = async (event) => {
        dispatch(alertActions.clear());
        event.preventDefault();

        let filesAdded = event.dataTransfer ? event.dataTransfer.files : event.target.files;

        if (multiple === false && filesAdded.length > 1) {
            filesAdded = [filesAdded[0]];
        }

        const newFileNames = [];
        const MAX_FILE_SIZE_MB = 10;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

        for (let i = 0; i < filesAdded.length; i += 1) {
            const file = filesAdded[i];
            newFileNames.push(file.name);

            file.extension = fileExtension(file);
            file.sizeReadable = fileSizeReadable(file.size);

            if (file.size > MAX_FILE_SIZE_BYTES) {
                handleError({ code: 'ERR_MSG_050', message: `File size cannot exceed ${MAX_FILE_SIZE_MB} MB` }, file);
                return;
            }

            if (!fileTypeAcceptable(supportedFormats, file)) {
                handleError({ code: 1, message: 'Invalid file format. Only Excel files are supported.' }, file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const arrayBuffer = e.target.result;
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

                if (jsonData.length === 0) {
                    handleError({ code: 2, message: 'The uploaded file contains no data.' }, file);
                    return;
                }

                const requiredHeaders = [
                    "Firm Group Name",
                    "Monthly Group Imbalance",
                    "Monthly Imbalance Adjustment",
                    "Ending Monthly Inventory Balance"
                ];


                
                const headerRowIndex = jsonData.findIndex(row => {
                    const rowValues = Object.values(row).filter(val => {
                        const strVal = String(val);
                        return strVal.trim() !== "";
                    });
                    return requiredHeaders.every(header => rowValues.includes(header));
                });

                if (headerRowIndex === -1) {
                    handleError({ code: 3, message: 'The uploaded file does not match the required format.' }, file);
                    return;
                }

                const headersRow = jsonData[headerRowIndex];
                const headerKeys = Object.keys(headersRow);

                const headerMap = {
                    AllocationGroup: headerKeys.find(k => headersRow[k] === 'Firm Group Name' || headersRow[k] === undefined),
                    MonthlyGroupImbalance: headerKeys.find(k => headersRow[k] === 'Monthly Group Imbalance'),
                    ImbalanceAdjustedVolume: headerKeys.find(k => headersRow[k] === 'Monthly Imbalance Adjustment'),
                    MonthEndImbalanceFirm: headerKeys.find(k => headersRow[k] === 'Ending Monthly Inventory Balance'),
                };

                const tableDetails = jsonData.slice(headerRowIndex + 1);
                if (!tableDetails || tableDetails.length === 0) {
                    handleError({ code: 3, message: 'The uploaded file contains no data.' }, file);
                    return;
                }

                let month = 'January';
                let datePublished = dayjs().format('YYYY-MM-DD');
                try {
                    const monthText = jsonData.find(row =>
                        Object.values(row)[0]?.toLowerCase()?.includes("month")
                    )?.[Object.keys(jsonData[0])[0]];

                    const dateText = jsonData.find(row =>
                        Object.values(row)[0]?.toLowerCase()?.includes("date published")
                    )?.[Object.keys(jsonData[0])[0]];

                    if (monthText?.toLowerCase().includes('month')) {
                        month = monthText.split(' ').pop();
                    }

                    if (dateText?.toLowerCase().includes('date')) {
                        datePublished = dateText.split(':').pop().trim();
                    }
                } catch {
                    handleError({ code: 5, message: 'Date or month format is incorrect in the file.' }, file);
                    return;
                }

                const year = new Date(datePublished).getFullYear();
                const currentDate = dayjs();
                const parsedDate = dayjs(`${year}-${month}-${currentDate.date()}`);
                const formattedDate = parsedDate.isValid() ? parsedDate.format('YYYY-MM-DDTHH:mm:ss') : dayjs().format('YYYY-MM-DDTHH:mm:ss');

                const tableData = tableDetails.map(row => ({
                    AllocationGroup: row[headerMap.AllocationGroup],
                    MonthlyGroupImbalance: row[headerMap.MonthlyGroupImbalance] && formatNumber(row[headerMap.MonthlyGroupImbalance]),
                    ImbalanceAdjustedVolume: row[headerMap.ImbalanceAdjustedVolume] && formatNumber(row[headerMap.ImbalanceAdjustedVolume]),
                    MonthEndImbalanceFirm: row[headerMap.MonthEndImbalanceFirm] && formatNumber(row[headerMap.MonthEndImbalanceFirm]),
                }));

                const allEmpty = tableData.every(item => item.ImbalanceAdjustedVolume === "");
                if(allEmpty) {
                    handleError({
                        code: 3,
                        message: 'Monthly Imbalance Adjustment column is empty.'
                    }, file);
                    return;
                }

                const validateData = tableData.slice();
                const validData = [];
                const invalidGroupNames = [];
                const inactiveGroupNames = [];

                validateData.forEach(row => {
                    const original = actualData.find(item => item.AllocationGroup.toLowerCase() === row.AllocationGroup.toLowerCase());
                    if (!original) invalidGroupNames.push(row.AllocationGroup);
                    else if (!original.IsActive) inactiveGroupNames.push(row.AllocationGroup);
                    else validData.push(row);
                });
console.log(invalidGroupNames,inactiveGroupNames)
                const updatedData = data.map(item => {
                    const adjustment = validData.find(adj => adj.AllocationGroup.toLowerCase() === item.AllocationGroup.toLowerCase());
                    return {
                        ...item,
                        ImbalanceAdjustedVolume: adjustment ? adjustment.ImbalanceAdjustedVolume : item.ImbalanceAdjustedVolume,
                        EffectiveDate: formattedDate,
                        isEditing: adjustment ? item.ImbalanceAdjustedVolume !== convertToNumber(adjustment?.ImbalanceAdjustedVolume) : false
                    };
                });

                setData(updatedData);
                setIsSaveButtonEnabled(data.length > 0);

                if (invalidGroupNames.length > 0 || inactiveGroupNames.length>0) {
                    let message=""
                    if(invalidGroupNames.length > 0)
                        message += `${invalidGroupNames.length} rows were skipped because they contain invalid firm group names.\n`;
                    if(inactiveGroupNames.length>0)
                        message += `${inactiveGroupNames.length} rows were skipped because they contain inactive firm group names.`;

                    dispatch(alertActions.error({ message }));
                    return;
                
                }
              
            };

            reader.readAsArrayBuffer(file);
        }

        setFileNames(newFileNames);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Typography component="div" className="UploadContainer">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Button
                        component="label"
                        variant="contained"
                        disabled={!hasUpdateSummaryAdjustmentsByFirmMarketerGroupsAccess}
                        className="Uploadfiles"
                        startIcon={<img src={materialsymbolsupload} alt="Upload" />}
                    >
                        <span> Upload your files here</span>
                        <VisuallyHiddenInput
                            type="file"
                            onChange={handleChange}
                            ref={fileInputRef}
                        />
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Typography component="div" className="SupportedFormats">
                        <Typography component="h3">Supported Formats</Typography>
                        <Typography component="div" className="fileformatlist">
                            {supportedFormats && supportedFormats.map(format => <span key={format}>{format} </span>)}
                        </Typography>
                    </Typography>
                </Grid>
                <Typography component="div" className="SupportedFormats">
                    <Typography component="h3">Download Template</Typography>
                    <div className="mar-top-16">
                        <Typography component="div">Sample File Format
                            <IconButton onClick={downloadSample}>
                                <img src={materialsymbolsdownload} alt="material-symbols_download" />
                            </IconButton>
                        </Typography>
                    </div>
                </Typography>

            </Grid>
        </Typography>
    );
};

export default FirmUpload;
